/**
 * Group switcher.
 *
 * People belong to several groups and move between them constantly, so this
 * sits at the top of the sidebar and is reachable from every screen. It shows
 * each group's storage inline, because "which one is nearly full" is the
 * question people actually have when they open it.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "@tanstack/react-router";
import { formatBytes, usageRatio } from "@fc/core";
import { CreateGroupDialog } from "./CreateGroupDialog.js";
import { JoinGroupDialog } from "./JoinGroupDialog.js";
import { placeMenu } from "./menuPlacement.js";
import "./GroupSwitcher.css";

export interface GroupSummary {
  id: string;
  name: string;
  emoji: string | null;
  accentColor: string;
  role: string;
  usedBytes: number;
  limitBytes: number;
  fileCount: number;
}

export interface GroupSwitcherProps {
  groups: GroupSummary[];
  currentGroupId: string | undefined;
  collapsed: boolean;
}

export function GroupSwitcher({
  groups,
  currentGroupId,
  collapsed,
}: GroupSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  /* The menu is portalled to <body>, so it is not inside containerRef and
     the dismiss handler has to be told about it separately — otherwise the
     first click on a group closes the menu instead of choosing it. */
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const current = groups.find((group) => group.id === currentGroupId);

  /* Where the menu goes, in viewport coordinates. The sidebar clips
     anything positioned inside it — see menuPlacement.ts. */
  const [placement, setPlacement] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (!open) return;

    const place = () => {
      const trigger = containerRef.current?.getBoundingClientRect();
      if (!trigger) return;

      setPlacement(
        placeMenu(
          trigger,
          { width: window.innerWidth, height: window.innerHeight },
          collapsed,
        ),
      );
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, collapsed]);

  /*
   * Typing narrows the list.
   *
   * Only shown past a handful, because a search box above four items is
   * furniture. Past that, scrolling a list to find "Slovenia" is slower than
   * typing three letters of it, and the list only grows.
   */
  const needle = query.trim().toLocaleLowerCase();
  const shown = needle
    ? groups.filter((group) => group.name.toLocaleLowerCase().includes(needle))
    : groups;

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="fc-switcher" ref={containerRef}>
      <button
        type="button"
        className="fc-switcher__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        title={collapsed ? (current?.name ?? "Groups") : undefined}
      >
        <span
          className="fc-switcher__emoji"
          data-accent={current?.accentColor || undefined}
          aria-hidden="true"
        >
          {/*
            A real icon rather than an emoji when no group is chosen. The
            placeholder was 🗂, which renders as an unrecognisable blob in the
            system emoji font on Windows — people asked what it was meant to
            be. A group's own emoji is still shown when it has one.
          */}
          {current?.emoji ?? (
            <svg viewBox="0 0 24 24" className="fc-switcher__placeholder">
              <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            </svg>
          )}
        </span>
        {!collapsed && (
          <>
            <span className="fc-switcher__name">
              {current?.name ?? "Choose a group"}
            </span>
            <svg
              className="fc-switcher__chevron"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </>
        )}
      </button>

      {open &&
        placement &&
        createPortal(
          <div
            className="fc-switcher__menu"
            role="menu"
            ref={menuRef}
            style={{
              position: "fixed",
              top: placement.top,
              left: placement.left,
              inlineSize: placement.width,
            }}
          >
            {/*
            Only past a handful: a search box above four items is furniture.
            Past that, scrolling to find "Slovenia" is slower than typing three
            letters of it, and the list only ever grows.
          */}
            {groups.length > 5 && (
              <input
                className="fc-switcher__search"
                type="search"
                value={query}
                autoFocus
                placeholder="Find a group"
                aria-label="Find a group"
                onChange={(event) => setQuery(event.target.value)}
              />
            )}

            <div className="fc-switcher__list">
              {shown.length === 0 && (
                <p className="fc-switcher__empty">
                  Nothing matches &ldquo;{query.trim()}&rdquo;.
                </p>
              )}
              {shown.map((group) => {
                const ratio = usageRatio({
                  limitBytes: group.limitBytes,
                  usedBytes: group.usedBytes,
                  reservedBytes: 0,
                  byClass: {
                    originals: 0,
                    derivatives: 0,
                    video: 0,
                    exports: 0,
                  },
                });

                return (
                  <button
                    key={group.id}
                    type="button"
                    role="menuitem"
                    className="fc-switcher__item"
                    data-current={group.id === currentGroupId || undefined}
                    onClick={() => {
                      setOpen(false);
                      void navigate({
                        to: "/g/$groupId",
                        params: { groupId: group.id },
                        search: {},
                      });
                    }}
                  >
                    <span
                      className="fc-switcher__item-emoji"
                      data-accent={group.accentColor || undefined}
                      aria-hidden="true"
                    >
                      {group.emoji ?? "🗂"}
                    </span>
                    <span className="fc-switcher__item-body">
                      <span className="fc-switcher__item-name">
                        {group.name}
                      </span>
                      <span className="fc-switcher__item-meta fc-tabular">
                        {group.fileCount.toLocaleString()} items ·{" "}
                        {formatBytes(group.usedBytes)}
                      </span>
                    </span>
                    <span
                      className="fc-switcher__item-gauge"
                      data-level={
                        ratio >= 0.95
                          ? "critical"
                          : ratio >= 0.8
                            ? "warning"
                            : "ok"
                      }
                      aria-hidden="true"
                    >
                      <span
                        style={{ transform: `scaleX(${Math.min(1, ratio)})` }}
                      />
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              role="menuitem"
              className="fc-switcher__create"
              onClick={() => {
                setOpen(false);
                setCreating(true);
              }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Create new group
            </button>

            {/* Beside creating one, because being invited is the more common of
              the two ways a group starts existing for somebody. */}
            <button
              type="button"
              role="menuitem"
              className="fc-switcher__create"
              onClick={() => {
                setOpen(false);
                setJoining(true);
              }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" />
              </svg>
              Join with a code
            </button>
          </div>,
          document.body,
        )}

      {joining && <JoinGroupDialog onClose={() => setJoining(false)} />}

      {creating && (
        <CreateGroupDialog
          onClose={() => setCreating(false)}
          onCreated={(groupId) => {
            setCreating(false);
            void navigate({
              to: "/g/$groupId",
              params: { groupId },
              search: {},
            });
          }}
        />
      )}
    </div>
  );
}
